import {
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  IconButton,
  Stack,
  Typography,
  Box
} from "@mui/material";
import React, { memo, useState } from "react";
import EditIcon from "../../Images/edit.png";
import DeleteIcon from "../../Images/delete.png";
import { Loader } from "../../Components/Loader";
import 'bootstrap-icons/font/bootstrap-icons.css';
import AttachmentPopup from "../../Components/AttachmentPopup";
import { RenderProfitCenterMultiple } from "../../HelperFunctions/MultiplePopUpTable";

const HeaderCell = ({ children, sx, ...props }) => (
  <TableCell
    component="th"
    scope="col"
    sx={{
      background: "linear-gradient(to bottom, #e6e6e6 0%, #f2f2f2 50%, #e6e6e6 100%)",
      borderTop: "1px solid #f2f2f2",
      borderBottom: "1px solid #f2f2f2",
      borderRight: "1px solid #f2f2f2",
      fontSize: "0.8rem",
      fontWeight: 500,
      color: "#000",
      padding: "4px 5px",
      height: "34.8px",
      boxSizing: "border-box",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 130,
      ...sx
    }}
    {...props}
  >
    {children}
  </TableCell>
);

const PayrollEntryListTable = ({ rows, columns, setEdit, onDelete, loading, error }) => {
  const [attachmentRow, setAttachmentRow] = useState(null);

  const tableMinWidth = columns.reduce((sum, col) => {
    if (typeof col.width === 'string' && (col.width.includes('%') || col.width === 'auto')) {
      return sum;
    }
    return sum + (Number(col.width) || 150);
  }, 0) + 150; // +150 for Actions column

  const ValueCell = memo(({ value, dataKey, row }) => {
    if (React.isValidElement(value)) {
      return value;
    }

    if (dataKey === 'profitCenter') {
      const pcName = value ?? row?.profitCenter ?? row?.raw?.PCName ?? "";
      const pcNames = row?.profitCenterNames ?? row?.raw?.PCNames ?? row?.raw?.pcNames ?? row?.raw?.ProfitCenterNames ?? "";

      if (String(pcName).trim().toLowerCase() === "multiple") {
        return <RenderProfitCenterMultiple pcNames={pcNames} />;
      }
      return <span title={pcName || ""}>{pcName || ""}</span>;
    }

    let displayValue = value !== null && value !== undefined ? value : "";

    if (dataKey === 'totalPayment') {
      const num = Number(value);
      if (!isNaN(num)) {
        displayValue = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    }
    return <span title={displayValue}>{displayValue}</span>;
  });

  const ActionButtons = memo(({ rowData }) => (
    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
      <span title="Edit">
        <IconButton size="small" onClick={() => setEdit(rowData)} sx={{ padding: "4px" }}>
          <img
            src={EditIcon}
            style={{ cursor: "pointer" }}
            alt="edit"
            width={18}
            height={18}
          />
        </IconButton>
      </span>
      <span title="Delete">
        <IconButton size="small" onClick={() => onDelete && onDelete(rowData)} sx={{ padding: "4px" }}>
          <img
            style={{ cursor: "pointer" }}
            src={DeleteIcon}
            alt="delete"
            width={18}
            height={18}
          />
        </IconButton>
      </span>
    </Stack>
  ));

  return (
    <>
    <TableContainer sx={{
      height: "100%",
      overflow: "auto",
      scrollbarGutter: "stable",
      "&::-webkit-scrollbar": {
        width: 5,
        height: 5
      },
      "&::-webkit-scrollbar-track": {
        marginTop: "40px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#bdbdbd",
        borderRadius: 10,
      }
    }}>
      <Table stickyHeader size="small" sx={{ width: "100%", minWidth: tableMinWidth, tableLayout: "fixed" }}>
        <TableHead
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 140,
            backgroundColor: "#ffffff",
          }}
        >
          <TableRow>
            {columns.map((col, idx) => (
              <HeaderCell 
                key={idx} 
                sx={{ 
                  width: col.width, 
                  minWidth: col.width, 
                  maxWidth: col.width, 
                textAlign: col.dataKey === 'status' || col.dataKey === 'totalPayment' || col.dataKey === 'employeeCount' || col.dataKey === 'isEntryUsed' || col.dataKey === 'entryType' ? 'center' : 'left' 
                }}
              >
                <span title={col.name}> {col.name} </span>
              </HeaderCell>
            ))}
            <HeaderCell sx={{ borderLeft: "1px solid #f2f2f2", width: 150, minWidth: 150, maxWidth: 150, textAlign: "center" }}>
              Actions
            </HeaderCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 10 }}>
                <Loader loading={loading} />
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                align="center"
                sx={{ py: 10 }}
              >
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                align="center"
                sx={{ py: 10 }}
              >
                <Typography variant="body2" color="text.secondary">
                  No data found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <TableRow key={row.id ?? index} hover sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                {columns.map((col, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      borderBottom: "1px solid #f2f2f2",
                      borderRight: "1px solid #f2f2f2",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#626262",
                      width: col.width,
                      minWidth: col.width,
                      maxWidth: col.width,
                      padding: "4px 5px",
                      height: "35px",
                      boxSizing: "border-box",                    
                    textAlign: col.dataKey === 'status' || col.dataKey === 'employeeCount' || col.dataKey === 'isEntryUsed' || col.dataKey === 'entryType' ? 'center' : col.dataKey === 'totalPayment' ? 'right' : 'left'
                    }}
                  >
                    {col.dataKey === 'isEntryUsed' ? (
                      <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
                        <Box sx={{ display: "flex", justifyContent: "center", width: 16 }}>
                          {row.raw?.ReconStatus === 6 && (
                            <i className="bi bi-check2-square" style={{ fontSize: "12px", }} title={"Reconciled"}></i>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center", width: 16 }}>
                          {row.raw?.HasAttachments && (
                            <i className="bi bi-link-45deg" style={{ fontSize: "12px", color: "#0d6efd", fontWeight: "600", cursor: "pointer" }} title="Attachments" onClick={() => setAttachmentRow(row)}></i>
                          )}
                        </Box>
                        
                      </Stack>
                    ) : (
                      <ValueCell value={row[col.dataKey]} dataKey={col.dataKey} row={row} />
                    )}
                  </TableCell>
                ))}

                <TableCell
                  sx={{
                    width: 150,
                    minWidth: 150,
                    maxWidth: 150,
                    height: "35px",
                    boxSizing: "border-box",
                    borderBottom: "1px solid #f2f2f2",
                    borderRight: "1px solid #f2f2f2",
                    padding: "0px 5px",
                    textAlign: "center",
                    verticalAlign: "middle"
                  }}
                >
                  <ActionButtons rowData={row} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
    <AttachmentPopup 
      open={Boolean(attachmentRow)} 
      onClose={() => setAttachmentRow(null)} 
      row={attachmentRow} 
    />
    </>
  );
};

export default PayrollEntryListTable;
